import { useFormContext } from "react-hook-form";
import { FieldProps } from ".";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, Locale, subYears, parseISO } from "date-fns";
import { DateField as ReusableDateField } from "@flightright/reusable-components";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { Domains, MaxDateType } from "@/core/types/types";
import {
  getTopLevelDomain,
  getDatePickerLocaleByTLD,
  getTranslationTexts,
} from "@/core/utils";
import { dateFormats } from "@/core/data";
import { HOTJAR_ATTRIBUTES as extraProps } from "@/core/data";
import { useEffect, useState } from "react";
import { useDefaultValue } from "@/core/hooks/useDefaultValue/useDefaultValue";

enum ErrorTypes {
  minDate = "minDate",
  maxDate = "maxDate",
  invalidDate = "invalidDate",
  required = "required",
}

type ErrorMessages = {
  [key in keyof typeof ErrorTypes as string]?: string;
};

type Error = {
  type: ErrorTypes;
  message?: string;
};

const TLD: Domains | string = getTopLevelDomain() || Domains.DE;
const MIN_DATE: Date = subYears(new Date(), 10);
const MAX_DATE: Date = subYears(new Date(), -3);
const ADOPTER_LOCALE: Locale = getDatePickerLocaleByTLD(TLD as Domains);
const TEXT: object = getTranslationTexts(TLD);

interface DateFieldProps extends FieldProps {
  initialValue?: Date;
}

const DateField = ({ field, initialValue }: DateFieldProps) => {
  const { defaultValue } = field;
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const [error, setError] = useState<Error | null>(null);

  const initialDateAsString: string | null = useDefaultValue(
    initialValue?.toISOString() ?? defaultValue
  );
  const dateFormat: string = dateFormats[TLD];
  const formStateError: boolean = !!errors[field.key];
  const formStateErrorMessage: string =
    errors[field.key]?.message?.toString() || "";

  const errorMessages: ErrorMessages = {
    [ErrorTypes.minDate]: t("validate.datePicker.minDate.message"),
    [ErrorTypes.maxDate]: t("validate.datePicker.maxDate.message"),
    [ErrorTypes.invalidDate]: t("validate.datePicker.invalidDate.message"),
    [ErrorTypes.required]: t(`validate.${field.key}.required.message`),
  };

  const validate = (): string | boolean => {
    const date: string | null | undefined = getValues(field.key);
    if (date !== null && error?.message) {
      return error.message;
    } else {
      return true;
    }
  };

  const handleChange = (value: Date | null, { validationError }: any) => {
    if (value == null || validationError !== null) {
      const err: Error = {
        type: validationError,
        message: errorMessages[validationError],
      };
      setError(err);
      setValue(field.key, value);
      return;
    }

    setError(null);
    setValue(field.key, format(value, "yyyy-MM-dd"));
  };

  const getMaxDate = (): Date => {
    const maxDate = field.validate?.maxDate;

    if (maxDate === MaxDateType.Today) {
      return new Date();
    } else if (maxDate) {
      return new Date(maxDate);
    }

    return MAX_DATE;
  };

  useEffect(() => {
    if (initialDateAsString) {
      const initialDate = parseISO(initialDateAsString);
      handleChange(initialDate, { validationError: null });
    }
  }, [initialDateAsString]);

  return (
    <ReusableDateField
      id={field.key}
      dateAdapter={AdapterDateFns}
      adapterLocale={ADOPTER_LOCALE}
      localeText={TEXT}
      name={field.key}
      control={control}
      rules={{
        required: {
          value: field.validate?.required || false,
          message: errorMessages.required || "",
        },
        validate,
      }}
      format={dateFormat}
      minDate={
        field.validate?.minDate ? new Date(field.validate?.minDate) : MIN_DATE
      }
      maxDate={getMaxDate()}
      label={t(field.label)}
      handleChange={handleChange}
      slotProps={{
        textField: {
          ...extraProps,
          error: formStateError,
          helperText: formStateErrorMessage,
        },
      }}
    />
  );
};

export default DateField;
